import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, TrendingDown, Zap, Award, Flame, Activity, Footprints, PersonStanding } from "lucide-react";
import { LucideIcon } from "lucide-react";

interface RealTimeFeedbackProps {
  currentPace: number;
  targetPace?: number;
  currentSpeed: number;
  duration: number;
  distance: number;
}

interface TrainingZone {
  text: string;
  icon: LucideIcon;
  color: string;
}

export const RealTimeFeedback = ({ 
  currentPace, 
  targetPace, 
  currentSpeed,
  duration,
  distance 
}: RealTimeFeedbackProps) => {
  const [feedback, setFeedback] = useState<string>("");
  const [feedbackType, setFeedbackType] = useState<'positive' | 'neutral' | 'warning'>('neutral');
  const [zone, setZone] = useState<TrainingZone>({ text: "Aquecimento", icon: Activity, color: "text-blue-500" });

  useEffect(() => {
    // Análise de ritmo em relação ao objetivo
    if (targetPace) {
      const paceDiff = currentPace - targetPace;
      
      if (Math.abs(paceDiff) < 0.2) {
        setFeedback("Perfeito! Mantendo o ritmo ideal");
        setFeedbackType('positive');
      } else if (paceDiff > 0.5) {
        setFeedback("Acelere um pouco! Você está abaixo do ritmo");
        setFeedbackType('warning');
      } else if (paceDiff < -0.5) {
        setFeedback("Ritmo muito rápido! Diminua a velocidade");
        setFeedbackType('warning');
      }
    }

    // Determinar zona de treino baseada no ritmo
    if (currentPace < 4) {
      setZone({ text: "Sprint", icon: Zap, color: "text-purple-500" });
    } else if (currentPace < 5) {
      setZone({ text: "Intenso", icon: Flame, color: "text-red-500" });
    } else if (currentPace < 6) {
      setZone({ text: "Moderado", icon: Activity, color: "text-orange-500" });
    } else if (currentPace < 7) {
      setZone({ text: "Leve", icon: Footprints, color: "text-blue-500" });
    } else {
      setZone({ text: "Recuperação", icon: PersonStanding, color: "text-green-500" });
    }

    // Feedback de progresso
    if (duration > 0 && duration % 300 === 0) { // A cada 5 minutos
      setFeedback(`Ótimo! Já são ${Math.floor(duration / 60)} minutos! Continue assim!`);
      setFeedbackType('positive');
    }

    // Feedback de distância
    if (distance > 0 && distance % 1 === 0) { // A cada km
      setFeedback(`Parabéns! Mais ${distance.toFixed(1)}km completos!`);
      setFeedbackType('positive');
    }

  }, [currentPace, targetPace, duration, distance]);

  const getPaceIcon = () => {
    if (!targetPace) return <Zap className="w-5 h-5" />;
    const diff = currentPace - targetPace;
    if (Math.abs(diff) < 0.2) return <Award className="w-5 h-5 text-green-500" />;
    if (diff > 0) return <TrendingDown className="w-5 h-5 text-orange-500" />;
    return <TrendingUp className="w-5 h-5 text-red-500" />;
  };

  return (
    <div className="space-y-3">
      {/* Zona de treino */}
      <Card className="p-4 bg-gradient-to-r from-primary/10 to-primary/5">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm text-muted-foreground">Zona de Treino</div>
            <div className="flex items-center gap-2 mt-1">
              <zone.icon className={`w-6 h-6 ${zone.color}`} />
              <span className="text-2xl font-bold">{zone.text}</span>
            </div>
          </div>
          {getPaceIcon()}
        </div>
      </Card>

      {/* Feedback em tempo real */}
      {feedback && (
        <Card className={`p-4 animate-in slide-in-from-bottom-2 ${
          feedbackType === 'positive' ? 'bg-green-500/10 border-green-500/20' :
          feedbackType === 'warning' ? 'bg-orange-500/10 border-orange-500/20' :
          'bg-blue-500/10 border-blue-500/20'
        }`}>
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <div className="font-medium">{feedback}</div>
            </div>
            <Badge variant={feedbackType === 'positive' ? 'default' : 'secondary'}>
              AI Coach
            </Badge>
          </div>
        </Card>
      )}

      {/* Comparação com ritmo alvo */}
      {targetPace && (
        <Card className="p-4">
          <div className="flex items-center justify-between text-sm">
            <div>
              <div className="text-muted-foreground">Pace Atual</div>
              <div className="text-xl font-bold">
                {Math.floor(currentPace)}:{String(Math.round((currentPace % 1) * 60)).padStart(2, '0')}
              </div>
            </div>
            <div className="text-right">
              <div className="text-muted-foreground">Target Pace</div>
              <div className="text-xl font-bold">
                {Math.floor(targetPace)}:{String(Math.round((targetPace % 1) * 60)).padStart(2, '0')}
              </div>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};
